<template>
	<div class="project-calendar">
		<header class="calendar-toolbar">
			<div>
				<span class="calendar-kicker">{{ $t('projects.calendar') }}</span>
				<h2 class="month-name">{{ monthName }}</h2>
			</div>
			<div class="calendar-navigation">
				<button type="button" class="calendar-nav-button icon-only" @click="$refs.calendar.prev()">
					<portal-icon name="chevron-left" :size="18"></portal-icon>
				</button>
				<button type="button" class="calendar-nav-button today-button" @click="today()">
					{{ $t('meetings.filter.today') }}
				</button>
				<button type="button" class="calendar-nav-button icon-only" @click="$refs.calendar.next()">
					<portal-icon name="chevron-right" :size="18"></portal-icon>
				</button>
			</div>
		</header>
		<div class="calendar-surface">
			<v-calendar
					id="calendar"
					ref="calendar"
					v-model="value"
					:weekdays="weekdays"
					@mouseenter:day="mouseMove"
					@mouseup:day="mouseUp"
					@mouseleave.native="mouseUp"
					type="month"
					class="calendar-grid"
			>
				<template v-slot:day-label="{ day }">
					<div class="dayLabel" v-if="day !== todayNumber">
						{{ day }}
					</div>
					<div v-else class="dayLabel">
						<div class="todayLabel">{{ day }}</div>
					</div>
				</template>

				<template v-slot:day="{ date }">
					<div class="day" :style="`--day-height: ${dayHeight}px;`" @click="addDeliverable(date)">
						<div v-for="(event, index) in events.get(date)" :key="event.id">
							<calendar-item
									v-if="index < maxItems || (index === maxItems && index === events.get(date).length - 1)"
									:event="event"
									@start-drag="startDrag($event)"
									@edit-deliverable="editDeliverable($event)"
                  :status="getStatus(event)"
							></calendar-item>
						</div>

						<v-menu
								:close-on-content-click="true"
								:nudge-top="dayHeight"
								content-class="calendar-overflow-menu"
								v-if="events.get(date) && events.get(date).length > maxItems + 1"
						>
							<template v-slot:activator="{ on }">
								<button type="button" class="has-more" v-on="on">
									+ {{ events.get(date).length - maxItems }} {{ $t('global.more') }}
								</button>
							</template>
							<div class="calendar-overflow-content">
								<header>
									<span>{{ moment(date).format('ddd') }}</span>
									<strong>{{ moment(date).format('D') }}</strong>
								</header>
								<div class="calendar-overflow-list">
									<calendar-item
										v-for="event in events.get(date)"
										:key="event.id"
										:event="event"
										@start-drag="startDrag($event)"
										@edit-deliverable="editDeliverable($event)"
										:status="getStatus(event)"
									></calendar-item>
								</div>
							</div>
						</v-menu>
					</div>
				</template>
			</v-calendar>
		</div>
	</div>
</template>

<script>
	import { canEditProject } from '@/modules/projects/projectLifecycle';
	import KanbanMixin from './ProjectMixin';
	import moment from 'moment';
	import CalendarItem from './CalendarItem';
	import PortalIcon from '@/components/PortalIcon.vue';

	export default {
		name: 'ProjectCalendar',

		mixins: [KanbanMixin],

		props: ['deliverables', 'projects', 'updateFlag', 'modalOpen', 'weekends','statusList'],

		components: { CalendarItem, PortalIcon },

		data: function() {
			return {
				weekdayOnly: false,
				value: new Date().toISOString().substr(0, 10),
				dragEvent: null,
				internalUpdate: 0,
				todayNumber: this.$DateTime.local().toFormat('d'),
				moment: moment,
				months: [
					'January',
					'February',
					'March',
					'April',
					'May',
					'June',
					'July',
					'August',
					'September',
					'October',
					'November',
					'December',
				],
				clickCount: 0,
				clickTimer: null,

			};
		},

		beforeMount() {
			this.startTime = Date.now();
		},

		mounted() {
			this.$store.commit('stopLoading')
			//this.$track.record('page-view', { module: 'project-calendar' });
			window.addEventListener('keydown', this.keyDown);
		},

		beforeDestroy() {
			window.removeEventListener('keydown', this.keyDown);
		},

		methods: {
			canEditProject,
      getStatus: function(event){
        let status = this.deliverableStatusMap.get(event.statusId);
        if(status){
          return status;
        }else{
          return {
            hexColor: '#eee',
            label: '[Unknown]'
          }
        }
      },
			keyDown: function(event) {
				if (this.modalOpen) {
					return;
				}

				switch (event.key) {
					case 'ArrowLeft':
						this.$refs.calendar.prev();
						break;
					case 'ArrowRight':
						this.$refs.calendar.next();
						break;
				}
			},

			editDeliverable: function(deliverable) {
				this.dragEvent = null;
				this.$emit('edit-deliverable', deliverable);
			},

			addDeliverable: function(date) {
				this.clickCount++;
				if (this.clickCount === 1) {
					this.clickTimer = setTimeout(() => {
						this.clickCount = 0;
					}, 500);
					return;
				}

				clearTimeout(this.clickTimer);
				this.clickCount = 0;

				this.dragEvent = null;
				this.$emit('add-deliverable-date', date);
			},

			checkAndSetMap: function(map, key, value) {
				if (!map.has(key)) {
					map.set(key, []);
				}

				map.get(key).push(value);
			},

			startDrag: function(event) {
				if (!canEditProject(event.project)) return;
				this.internalUpdate++;
				this.dragEvent = event;
			},

			mouseMove: function(event) {
				if (this.dragEvent) {
					this.internalUpdate++;
					this.dragEvent.dueDate = event.date;
				}
			},

			mouseUp: function() {
				if (this.dragEvent) {
					this.internalUpdate++;
					this.$emit('update-due-date', this.dragEvent);
					this.dragEvent = null;
				}
			},

			today: function() {
				let m = moment();
				this.value = m.format('YYYY-MM-DD');
			},
		},

		computed: {
      deliverableStatusMap: function() {
        let statusMap = new Map();
        if (this.statusList && this.statusList) {
          for (let i = 0; i < this.statusList.length; i++) {
            let s = this.statusList[i];
            statusMap.set(s.id, s);
          }
        }
        return statusMap;
      },

			weekdays: function() {
        return [0, 1, 2, 3, 4, 5, 6];
			},

			dayHeight: function() {
				return Math.floor((this.$vuetify.breakpoint.height - 380) / this.weekCount);
			},

			maxItems: function() {
				let maxItems = Math.floor(this.dayHeight / 50);
				return maxItems < 1 ? 1 : maxItems;
			},

			weekCount: function() {
				let ourDate = moment(this.value, 'YYYY-MM-DD');

				let firstOfMonth = new Date(ourDate.year(), ourDate.month(), 1);
				let lastOfMonth = new Date(ourDate.year(), ourDate.month() + 1, 0);

				let used = firstOfMonth.getDay() + lastOfMonth.getDate();

				return Math.ceil(used / 7);
			},

			monthName: function() {
				let m = moment(this.value, 'YYYY-MM-DD');
				return m.format('MMMM YYYY');
			},

			events: function() {
				if (this.updateFlag) {
					//do nothing;
				}

				if (this.internalUpdate) {
					//do nothing
				}

				let result = new Map();
        let localDeliverables = JSON.parse(JSON.stringify(this.deliverables));

				localDeliverables.forEach((d) => {
					if (d.dueDate) {
						if (d.dueDate) {
							this.checkAndSetMap(result, d.dueDate, d);
						} else {
							this.checkAndSetMap(result, 'no-date', d);
						}
					}
				});
				return result;
			},
		},
	};
</script>

<style scoped lang="scss">
	.project-calendar {
		min-width: 0;
		padding: 0 14px 18px;
	}

	.calendar-toolbar {
		min-height: 66px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 10px 4px 12px;
	}

	.calendar-kicker {
		display: block;
		margin-bottom: 2px;
		color: var(--portal-muted);
		font-size: 11px;
		font-weight: 650;
		letter-spacing: 0.04em;
		text-transform: uppercase;
	}

	.month-name {
		margin: 0;
		color: var(--portal-text);
		font-size: 20px;
		font-weight: 650;
		letter-spacing: -0.02em;
	}

	.calendar-navigation {
		display: flex;
		align-items: center;
		gap: 5px;
		padding: 4px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-button-radius, 12px);
		background: var(--portal-surface);
	}

	.calendar-nav-button {
		min-height: 34px;
		display: grid;
		place-items: center;
		padding: 0 11px;
		border: 0;
		border-radius: var(--brand-button-radius, 8px);
		background: transparent;
		color: var(--portal-muted);
		font: inherit;
		font-size: 12px;
		font-weight: 650;
		cursor: pointer;
	}

	.calendar-nav-button.icon-only {
		width: 34px;
		padding: 0;
	}

	.calendar-nav-button:hover,
	.today-button {
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	.calendar-surface {
		min-width: 0;
		overflow: hidden;
		border: 1px solid var(--portal-border);
		border-radius: var(--portal-radius-md);
		background: var(--portal-surface);
	}

	.calendar-grid {
		height: clamp(520px, calc(100vh - 330px), 820px);
	}

	.day {
		min-height: 55px;
		padding: 5px;
		height: var(--day-height);
	}

	.has-more {
		width: 100%;
		padding: 3px 6px;
		border: 0;
		border-radius: var(--brand-button-radius, 7px);
		background: transparent;
		text-align: left;
		font-size: 12px;
		font-weight: 650;
		color: var(--v-primary-base);
		cursor: pointer;
	}

	.has-more:hover {
		background: color-mix(in srgb, var(--v-primary-base) 7%, transparent);
	}

	.dayLabel {
		font-size: 12px;
		font-weight: 600;
		text-align: left;
		padding: 5px 0 3px 7px;
		color: var(--portal-muted);
	}

	.todayLabel {
		background-color: var(--v-primary-base);
		color: var(--v-white-base);
		width: 22px;
		height: 22px;
		display: grid;
		place-items: center;
		border-radius: 50%;
	}

	#calendar ::v-deep .v-calendar-weekly__head-weekday {
		text-transform: none;
		padding: 9px 7px;
		text-align: left;
		border-color: var(--portal-border);
		background-color: var(--v-gray_20-base) !important;
		color: var(--portal-muted);
		font-size: 11px;
		font-weight: 650;
	}

	#calendar ::v-deep .v-outside {
		background-color: var(--v-gray_10-base);
		opacity: 0.62;
	}

	#calendar ::v-deep .v-calendar-weekly__day,
	#calendar ::v-deep .v-calendar-weekly__head-weekday {
		border-color: var(--portal-border) !important;
	}

	@media (max-width: 699px) {
		.project-calendar {
			padding: 0 10px 16px;
		}

		.calendar-toolbar {
			align-items: flex-end;
		}

		.calendar-kicker {
			display: none;
		}

		.month-name {
			font-size: 17px;
		}

		.calendar-nav-button.today-button {
			display: none;
		}

		.calendar-grid {
			height: 600px;
			min-width: 660px;
		}

		.calendar-surface {
			overflow-x: auto;
		}
	}
</style>

<style lang="scss">
	.calendar-overflow-menu {
		width: min(300px, calc(100vw - 24px));
		max-height: min(420px, calc(100vh - 48px));
		overflow: auto;
		padding: 8px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 14px) !important;
		background: var(--portal-surface);
		box-shadow: 0 18px 45px rgba(16, 17, 15, 0.18) !important;
	}

	.calendar-overflow-content > header {
		display: flex;
		align-items: baseline;
		gap: 6px;
		padding: 8px 9px 10px;
		border-bottom: 1px solid var(--portal-border);
		color: var(--portal-muted);
		font-size: 12px;
	}

	.calendar-overflow-content > header strong {
		color: var(--portal-text);
		font-size: 18px;
	}

	.calendar-overflow-list {
		display: grid;
		gap: 5px;
		padding-top: 8px;
	}
</style>
